import React, { useEffect, useState } from 'react';
import { auth, db, googleProvider } from './firebase-config';
import {
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  getRedirectResult,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
} from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { buildControlUserProfile, isControlAccessEmail } from './core/security/controlAccess';

const Icon = ({ path, className = 'w-6 h-6' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

const currentAuthDomainHint = () => {
  if (typeof window === 'undefined') return 'este dominio';
  return window.location.hostname || 'este dominio';
};

const resolveFriendlyAuthError = (error) => {
  const code = String(error?.code || '').trim();

  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-login-credentials':
      return 'Correo o contraseña incorrectos. Revisá tus datos e intentá de nuevo.';
    case 'auth/invalid-email':
      return 'El correo ingresado no es válido.';
    case 'auth/too-many-requests':
      return 'Se bloquearon temporalmente los intentos. Esperá un momento y volvé a probar.';
    case 'auth/popup-closed-by-user':
      return 'Se cerró la ventana antes de completar el acceso.';
    case 'auth/popup-blocked':
    case 'auth/cancelled-popup-request':
      return 'El navegador bloqueó o cerró la ventana de Google. Probá con el acceso sin ventana emergente.';
    case 'auth/unauthorized-domain':
      return `Este dominio todavía no está autorizado en Firebase Authentication. Agregá ${currentAuthDomainHint()} en Authentication > Settings > Authorized domains.`;
    case 'auth/configuration-not-found':
      return 'En el proyecto Firebase nuevo todavía falta terminar Authentication. Activá Authentication, habilitá Google en Sign-in method y definí el correo de asistencia del proyecto.';
    case 'auth/operation-not-allowed':
      return 'Google todavía no está habilitado como proveedor de acceso en Firebase Authentication.';
    default:
      return error?.message || 'No pudimos completar el acceso en este momento.';
  }
};

const Auth = ({
  showMessage,
  title = 'Ingresar',
  subtitle = 'Ingresá a tu espacio de trabajo.',
  allowRegister = true,
  allowGoogle = true,
  allowNewGoogleUsers = allowRegister,
  brandLabel = 'G-CENTRA',
  contextLabel = '',
  backLabel = '',
  onBack = null,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [inlineError, setInlineError] = useState('');

  useEffect(() => {
    if (!allowRegister) setIsLogin(true);
  }, [allowRegister]);

  const getFriendlyAuthError = (error) => {
    const code = String(error?.code || '').trim();

    switch (code) {
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
      case 'auth/invalid-login-credentials':
        return 'Correo o contraseña incorrectos. Revisalos y probá de nuevo.';
      case 'auth/invalid-email':
        return 'El correo ingresado no es válido.';
      case 'auth/email-already-in-use':
        return 'Ya existe una cuenta con este correo.';
      case 'auth/too-many-requests':
        return 'Demasiados intentos. Esperá un momento y volvé a intentar.';
      case 'auth/popup-closed-by-user':
        return 'Se cerró la ventana de Google antes de completar el acceso.';
      case 'auth/popup-blocked':
      case 'auth/cancelled-popup-request':
        return 'El navegador bloqueó o cerró la ventana de Google. Probá con el acceso sin ventana emergente.';
      case 'auth/unauthorized-domain':
        return `Este dominio todavía no está autorizado en Firebase Authentication. Agregá ${currentAuthDomainHint()} en Authentication > Settings > Authorized domains.`;
      case 'auth/configuration-not-found':
        return 'En el proyecto Firebase nuevo todavía falta terminar Authentication. Activá Authentication, habilitá Google en Sign-in method y definí el correo de asistencia del proyecto.';
      case 'auth/operation-not-allowed':
        return 'Google todavía no está habilitado como proveedor de acceso en Firebase Authentication.';
      default:
        return error?.message || 'No pudimos autenticarte en este momento.';
    }
  };

  const ensureLegacyUserDoc = async (user) => {
    const profile = isControlAccessEmail(user.email)
      ? buildControlUserProfile(user)
      : {
          uid: user.uid,
          email: user.email,
          role: 'transportista',
        };

    await setDoc(
      doc(db, 'users', user.uid),
      {
        ...profile,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  };

  const finishGoogleSignIn = async (result) => {
    const user = result.user;
    const additionalInfo = getAdditionalUserInfo(result);

    if (additionalInfo?.isNewUser && !allowNewGoogleUsers) {
      const message = 'Este acceso no permite registrar cuentas nuevas desde Google.';
      setInlineError(message);
      showMessage(message, 'error', 7000);
      return;
    }

    if (additionalInfo?.isNewUser || isControlAccessEmail(user.email)) {
      await ensureLegacyUserDoc(user);
      showMessage(
        isControlAccessEmail(user.email)
          ? 'Acceso interno G-CENTRA habilitado.'
          : 'Usuario registrado con Google exitosamente.',
        'success'
      );
    } else {
      showMessage('Inicio de sesión con Google exitoso.', 'success');
    }
  };

  useEffect(() => {
    let mounted = true;

    const resolveRedirect = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (!mounted || !result) return;

        setLoading(true);
        setInlineError('');
        await finishGoogleSignIn(result);
      } catch (error) {
        console.error('Error con Google Redirect:', error);
        const friendlyMessage = getFriendlyAuthError(error);
        if (mounted) setInlineError(friendlyMessage);
        showMessage(friendlyMessage, 'error', 7000);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    resolveRedirect();

    return () => {
      mounted = false;
    };
    // Solo se resuelve una vez al volver desde Google.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setInlineError('');
    try {
      const normalizedEmail = email.trim().toLowerCase();

      if (isLogin || !allowRegister) {
        await signInWithEmailAndPassword(auth, normalizedEmail, password);
        showMessage('Inicio de sesión exitoso.', 'success');
      } else {
        const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
        await ensureLegacyUserDoc(userCredential.user);
        showMessage('Usuario registrado con éxito.', 'success');
      }
    } catch (error) {
      console.error('Error de autenticación:', error);
      const friendlyMessage = resolveFriendlyAuthError(error);
      setInlineError(friendlyMessage);
      showMessage(friendlyMessage, 'error', 7000);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (!allowGoogle) return;

    setLoading(true);
    setInlineError('');
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await finishGoogleSignIn(result);
    } catch (error) {
      console.error('Error con Google Sign-In:', error);
      const friendlyMessage = getFriendlyAuthError(error);
      setInlineError(friendlyMessage);
      showMessage(friendlyMessage, 'error', 7000);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRedirectSignIn = async () => {
    if (!allowGoogle) return;

    setLoading(true);
    setInlineError('');
    try {
      await signInWithRedirect(auth, googleProvider);
    } catch (error) {
      console.error('Error con Google Redirect:', error);
      const friendlyMessage = getFriendlyAuthError(error);
      setInlineError(friendlyMessage);
      showMessage(friendlyMessage, 'error', 7000);
      setLoading(false);
    }
  };

  const cardTitle = isLogin || !allowRegister ? 'Iniciar sesión' : 'Crear acceso';

  return (
    <div className="min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top,rgba(8,145,178,0.22),transparent_22%),radial-gradient(circle_at_82%_18%,rgba(59,130,246,0.18),transparent_20%),linear-gradient(180deg,#020617_0%,#0f172a_55%,#111827_100%)] px-4 py-8 text-white">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(115deg,rgba(255,255,255,0.03),transparent_18%,transparent_78%,rgba(34,211,238,0.04))]" />
      <div className="pointer-events-none absolute left-[8%] top-[10%] h-40 w-40 rounded-full bg-cyan-400/10 blur-3xl" />
      <div className="pointer-events-none absolute right-[10%] top-[14%] h-48 w-48 rounded-full bg-sky-400/10 blur-3xl" />
      <div className="mx-auto flex min-h-screen max-w-6xl items-center justify-center">
        <div className="grid w-full gap-6 lg:grid-cols-[0.95fr_1.05fr] lg:items-stretch">
          <section className="relative overflow-hidden rounded-[32px] border border-cyan-400/15 bg-[linear-gradient(145deg,rgba(2,6,23,0.96),rgba(15,23,42,0.96)_42%,rgba(8,47,73,0.94)_100%)] p-8 shadow-[0_28px_80px_rgba(2,6,23,0.46)]">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.08),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(34,211,238,0.12),transparent_26%)]" />
            <div className="pointer-events-none absolute -right-12 top-8 h-40 w-40 rounded-full border border-white/10 bg-white/5 blur-2xl" />
            <div className="relative z-10">
              {contextLabel ? <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-300">{contextLabel}</p> : null}
              <div className="mt-1 flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center rounded-full border border-cyan-300/25 bg-cyan-400/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.2em] text-cyan-200">
                  Acceso seguro
                </span>
                <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-300">
                  Plataforma operativa
                </span>
              </div>
              <h1 className="mt-4 text-6xl font-black tracking-[-0.08em] text-white sm:text-7xl">{brandLabel}</h1>
              <div className="mt-4 h-px w-28 bg-gradient-to-r from-cyan-400 via-white/80 to-transparent" />
              <h2 className="mt-6 max-w-xl text-3xl font-black tracking-tight text-white sm:text-4xl">{title}</h2>
              <p className="mt-4 max-w-xl text-sm leading-7 text-slate-300 sm:text-base">{subtitle}</p>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                <div className="rounded-[22px] border border-white/10 bg-white/5 px-4 py-4">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-cyan-200">Logística</p>
                  <p className="mt-2 text-sm leading-6 text-slate-300">Rutas, unidades y operación diaria en una sola lectura.</p>
                </div>
                <div className="rounded-[22px] border border-white/10 bg-white/5 px-4 py-4">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-cyan-200">Stock</p>
                  <p className="mt-2 text-sm leading-6 text-slate-300">Productos, movimientos y control conectados con la calle.</p>
                </div>
                <div className="rounded-[22px] border border-white/10 bg-white/5 px-4 py-4">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-cyan-200">Trazabilidad</p>
                  <p className="mt-2 text-sm leading-6 text-slate-300">Seguimiento operativo con crecimiento según el nivel del plan.</p>
                </div>
              </div>

              <div className="mt-8 rounded-[26px] border border-white/10 bg-white/6 p-5 shadow-[0_18px_40px_rgba(2,6,23,0.22)]">
                <p className="text-sm font-semibold text-white">Ingreso simple y directo.</p>
                <p className="mt-2 text-sm leading-6 text-slate-300">
                  Accedé con tu cuenta para continuar con tu operación, revisar tus recorridos, leer el estado de tu empresa y mantener toda la gestión en un solo lugar.
                </p>
              </div>

              {onBack && backLabel ? (
                <button
                  type="button"
                  onClick={onBack}
                  className="mt-8 inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-white/10"
                >
                  <Icon path="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" className="h-4 w-4" />
                  {backLabel}
                </button>
              ) : null}
            </div>
          </section>

          <section className="relative w-full overflow-hidden rounded-[32px] border border-white/15 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-8 text-slate-900 shadow-[0_30px_80px_rgba(15,23,42,0.32)]">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.16),transparent_60%)]" />
            <div className="text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[22px] bg-[linear-gradient(145deg,#0891b2,#0f172a)] text-white shadow-[0_18px_36px_rgba(8,145,178,0.24)]">
                <Icon path="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" className="h-8 w-8" />
              </div>
              <p className="mt-5 text-[11px] font-black uppercase tracking-[0.22em] text-cyan-700">Acceso a tu espacio</p>
              <h3 className="mt-3 text-3xl font-black tracking-tight text-slate-900">{cardTitle}</h3>
              <p className="mt-2 text-sm text-slate-500">
                Entrá con tu cuenta y retomá tu operación desde donde la dejaste.
              </p>
            </div>

            <form className="mt-8 space-y-5" onSubmit={handleAuth}>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setInlineError('');
                }}
                placeholder="Correo electrónico"
                autoComplete="username"
                required
                className="w-full rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-3.5 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
              />
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setInlineError('');
                }}
                placeholder="Contraseña"
                autoComplete="current-password"
                required
                className="w-full rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-3.5 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
              />
              {inlineError ? (
                <div className="rounded-[18px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold leading-6 text-red-700">
                  {inlineError}
                </div>
              ) : null}
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-[20px] bg-[linear-gradient(145deg,#0891b2,#0e7490)] px-4 py-3.5 font-semibold text-white shadow-[0_16px_32px_rgba(8,145,178,0.22)] transition hover:-translate-y-0.5 hover:from-cyan-600 hover:to-cyan-700 disabled:translate-y-0 disabled:from-cyan-300 disabled:to-cyan-300"
              >
                {loading ? 'Ingresando...' : isLogin || !allowRegister ? 'Entrar' : 'Crear cuenta'}
              </button>
            </form>

            {allowGoogle ? (
              <>
                <div className="mt-6 flex items-center justify-between"><hr className="w-full border-slate-200" /><span className="p-2 text-xs text-slate-400">O</span><hr className="w-full border-slate-200" /></div>
                <button
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-[20px] border border-slate-200 bg-white px-4 py-3.5 font-semibold text-slate-700 transition hover:bg-slate-50 disabled:bg-slate-100"
                >
                  <svg className="h-5 w-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"></path><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"></path><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"></path><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.443-2.57 4.473-4.902 5.713l6.19 5.238C39.712 35.619 44 29.561 44 24c0-1.341-.138-2.65-.389-3.917z"></path></svg>
                  <span>Continuar con Google</span>
                </button>
                <button
                  type="button"
                  onClick={handleGoogleRedirectSignIn}
                  disabled={loading}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-[20px] border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm font-bold text-cyan-800 transition hover:bg-cyan-100 disabled:bg-slate-100 disabled:text-slate-400"
                >
                  Google sin ventana emergente
                </button>
              </>
            ) : null}

            {allowRegister ? (
              <p className="mt-6 text-center text-sm text-slate-600">
                {isLogin ? '¿Todavía no tenés cuenta?' : '¿Ya tenés una cuenta?'}
                <button type="button" onClick={() => setIsLogin(!isLogin)} className="ml-1 font-medium text-cyan-700 hover:underline">
                  {isLogin ? 'Crear cuenta' : 'Iniciar sesión'}
                </button>
              </p>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
};

export default Auth;
